/**
 * Project sub-tabs — Iter 117 batch.
 * - ProjectDashboardTab: AI summary + risk radar + team health tiles.
 * - ProjectStatusUpdatesTab: feed + composer + AI draft.
 * - ProjectTasksSubTab / ProjectIssuesSubTab / ProjectGoalsSubTab: filtered views.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Loader2, Sparkles, AlertTriangle, TrendingUp, Users, MessageSquare, Eye, Lock, Trash2, Edit3, Save, X, RotateCcw, CheckSquare, Target, Bug, ArrowUpRight, CalendarClock, Mic, Square, Search as SearchIcon, ChevronDown, LayoutGrid, Plus, ArrowUp } from "lucide-react";
import { apiErrorMessage } from "@/lib/apiError";
import SmartRichText, { SmartRichTextDisplay } from "@/components/SmartRichText";
import { useAuth } from "@/lib/auth";
import { fmtRelative } from "@/lib/format";
import TaskViewSwitcher from "@/components/tasks/TaskViewSwitcher";
import TaskBillingView from "@/components/tasks/TaskBillingView";
import { TaskDetail, InlineDateCell, isOverdue, TaskTitleCell, PRIORITIES, STATUSES } from "@/pages/app/Tasks";
import askDateChangeReason from "@/components/tasks/DateChangeReasonDialog";
import { BulkActionBar } from "@/components/tasks/TaskRiskAndBulk";
import { QuickAddTaskPopover, QuickAddTaskInline } from "@/components/tasks/QuickAddTask";
import BulkTaskCreator from "@/components/tasks/BulkTaskCreator";
// R100 · Share the exact same column config as the main Tasks list so the
// Projects > Tasks sub-tab renders every rich field (client, brand,
// priority, health, planned start/end with inline edit, timesheet hours,
// billable/billed/paid, etc.). Table itself renders via AdvancedTable.
import AdvancedTable from "@/components/table/AdvancedTable";
import { buildTaskColumns } from "@/components/tasks/buildTaskColumns";
import ProjectPhasesStrip from "@/components/project/ProjectPhasesStrip";
import useWorkspacePerms from "@/lib/useWorkspacePerms";
import { PersonChip } from "@/components/PersonChip";

// ── Dashboard ─────────────────────────────────────────────────────────────

export function ProjectDashboardTab({ projectId, project }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try { const r = await api.get(`/projects/${projectId}/dashboard`); setData(r.data); }
    catch (e) { toast.error(apiErrorMessage(e, "Failed to load dashboard")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line

  if (loading || !data) return <div className="flex items-center gap-2 text-xs text-muted-foreground p-6"><Loader2 className="w-4 h-4 animate-spin" /> Analysing project…</div>;
  const { tiles, ai } = data;
  const healthColor = { strong: "text-emerald-600", steady: "text-blue-600", thin: "text-amber-600", overloaded: "text-red-600" }[ai.team_health] || "text-foreground";

  return (
    <div className="space-y-4" data-testid="project-dashboard">
      {ai.summary && (
        <div className="border border-primary/40 bg-primary/5 p-4" data-testid="project-dashboard-summary">
          <div className="flex items-center gap-2 mb-2 text-xs font-semibold uppercase tracking-widest"><Sparkles className="w-3.5 h-3.5 text-primary" /> How&apos;s this project going?</div>
          <p className="text-sm">{ai.summary}</p>
        </div>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <StatTile label="Milestones" value={`${tiles.milestones.done}/${tiles.milestones.total}`} sub={`${tiles.milestones.at_risk} at risk`} icon={CheckSquare} />
        <StatTile label="Tasks (14d)" value={tiles.tasks?.recent_count || 0} sub={`${tiles.tasks?.done_recent || 0} done · ${tiles.tasks?.blocked || 0} blocked`} icon={TrendingUp} />
        <StatTile label="Team hours (14d)" value={tiles.timesheet?.total_hours?.toFixed?.(1) || "0"} sub={`${tiles.timesheet?.active_people || 0} active people`} icon={Users} />
        <StatTile label="Updates posted" value={tiles.updates_last_14d} sub="last 14 days" icon={MessageSquare} />
      </div>
      <div className="grid lg:grid-cols-2 gap-3">
        <div className="border border-border p-4" data-testid="project-dashboard-risks">
          <div className="flex items-center gap-2 mb-2 text-xs font-semibold uppercase tracking-widest"><AlertTriangle className="w-3.5 h-3.5 text-amber-600" /> Risk radar</div>
          {(ai.risks || []).length === 0 ? <div className="text-xs text-muted-foreground italic">No material risks detected.</div> : (
            <ul className="space-y-2">
              {ai.risks.map((r, i) => (
                <li key={i} className="border-l-2 pl-3 py-1" style={{ borderColor: r.severity === "high" ? "#dc2626" : r.severity === "medium" ? "#d97706" : "#94a3b8" }} data-testid={`project-dashboard-risk-${i}`}>
                  <div className="text-sm font-semibold flex items-center gap-1.5">{r.title} <span className="text-[10px] uppercase text-muted-foreground">{r.severity}</span></div>
                  {r.why && <div className="text-xs text-muted-foreground">{r.why}</div>}
                </li>
              ))}
            </ul>
          )}
        </div>
        <div className="border border-border p-4" data-testid="project-dashboard-team">
          <div className="flex items-center gap-2 mb-2 text-xs font-semibold uppercase tracking-widest"><Users className="w-3.5 h-3.5" /> Team health</div>
          {ai.team_health ? (
            <>
              <div className={`text-lg font-bold capitalize ${healthColor}`}>{ai.team_health}</div>
              {ai.team_health_note && <p className="text-xs text-muted-foreground mt-1">{ai.team_health_note}</p>}
            </>
          ) : <div className="text-xs text-muted-foreground italic">Not enough signal yet.</div>}
          {tiles.timesheet?.top_contributors?.length > 0 && (
            <div className="mt-3">
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Top contributors</div>
              <ul className="text-xs space-y-0.5">
                {tiles.timesheet.top_contributors.map((c, i) => (
                  <li key={i}>{(c[0] || "—").slice(0, 12)} — {c[1].toFixed(1)}h</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
      <div className="text-[10px] text-muted-foreground flex items-center gap-2">
        <button type="button" onClick={load} className="hover:underline flex items-center gap-1"><RotateCcw className="w-3 h-3" /> Refresh</button>
        · Insights generated by Claude · project: {project?.name}
      </div>
    </div>
  );
}

function StatTile({ label, value, sub, icon: Icon }) {
  return (
    <div className="border border-border p-3">
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-muted-foreground"><Icon className="w-3 h-3" />{label}</div>
      <div className="text-2xl font-bold mt-1">{value}</div>
      {sub && <div className="text-[10px] text-muted-foreground">{sub}</div>}
    </div>
  );
}

// ── Status Updates ─────────────────────────────────────────────────────────

export function ProjectStatusUpdatesTab({ projectId, project, onPosted }) {
  const { activeWorkspaceId, user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [visibility, setVisibility] = useState("internal");
  const [drafting, setDrafting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editId, setEditId] = useState(null);
  const [editBody, setEditBody] = useState("");
  // R69 · Mention picker (in-app notifications only) + members
  const [members, setMembers] = useState([]);
  const [mentionIds, setMentionIds] = useState([]);
  const [showMentionPicker, setShowMentionPicker] = useState(false);
  const [converting, setConverting] = useState(null);  // update id being converted

  const load = async () => {
    setLoading(true);
    try { const r = await api.get(`/projects/${projectId}/status-updates`); setRows(r.data || []); }
    catch (e) { toast.error(apiErrorMessage(e, "Failed to load updates")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [projectId]); // eslint-disable-line

  // R69 · Lazy-load workspace members once, only when the composer is used.
  useEffect(() => {
    const wsId = activeWorkspaceId || project?.workspace_id;
    if (!wsId || members.length) return;
    let cancelled = false;
    api.get(`/workspaces/${wsId}/members`)
      .then(({ data }) => {
        if (cancelled) return;
        const list = Array.isArray(data) ? data : (data?.members || []);
        setMembers(list);
      })
      .catch(() => { /* ignore, mention picker just won't populate */ });
    return () => { cancelled = true; };
  }, [activeWorkspaceId, project?.workspace_id, members.length]);

  const post = async () => {
    if (!body.trim()) return;
    setSaving(true);
    try {
      await api.post(`/projects/${projectId}/status-updates`, {
        body_html: body,
        visibility,
        kind: "manual",
        mention_user_ids: mentionIds,
      });
      setBody(""); setMentionIds([]); setShowMentionPicker(false);
      toast.success(mentionIds.length ? `Update posted · ${mentionIds.length} notified` : "Update posted");
      load();
      // R159n · Feb 2026 — Let the caller (e.g. Planning Board Present
      // mode) know a new update just landed so it can refresh the card
      // status pill live.
      onPosted && onPosted();
    } catch (e) { toast.error(apiErrorMessage(e, "Post failed")); }
    finally { setSaving(false); }
  };

  const aiDraft = async () => {
    setDrafting(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/status-updates/ai-draft`);
      setBody(data.body_html || "");
      toast.success("Draft ready — review and post");
    } catch (e) { toast.error(apiErrorMessage(e, "AI draft failed")); }
    finally { setDrafting(false); }
  };

  const [digesting, setDigesting] = useState(false);
  const postWeeklyDigest = async () => {
    setDigesting(true);
    try {
      await api.post(`/projects/${projectId}/status-updates/weekly-digest`);
      toast.success("Weekly digest posted to the feed");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Digest failed")); }
    finally { setDigesting(false); }
  };

  // R73 · Voice-to-text: mic button records audio in the browser, ships
  // the blob to /sops/transcribe (Whisper), and appends the transcribed
  // text to the composer body. Reuses the SOP-generator pattern.
  const [recording, setRecording] = useState(false);
  const [transcribing, setTranscribing] = useState(false);
  const recRef = useRef(null);
  const toggleRecord = async () => {
    if (recording) { recRef.current?.stop(); return; }
    if (!navigator.mediaDevices?.getUserMedia) {
      toast.error("Voice capture isn't supported on this browser");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mr = new MediaRecorder(stream, {
        mimeType: MediaRecorder.isTypeSupported("audio/webm") ? "audio/webm" : undefined,
      });
      const chunks = [];
      mr.ondataavailable = (e) => { if (e.data.size) chunks.push(e.data); };
      mr.onstop = async () => {
        stream.getTracks().forEach((t) => t.stop());
        setRecording(false);
        const blob = new Blob(chunks, { type: mr.mimeType || "audio/webm" });
        if (blob.size < 1000) { toast.error("Recording too short"); return; }
        setTranscribing(true);
        try {
          const fd = new FormData();
          fd.append("file", blob, "project-update-voice.webm");
          const { data } = await api.post("/sops/transcribe", fd);
          let text = (data.text || "").trim();
          if (!text) { toast.error("No speech detected"); return; }
          // R73b · Light AI cleanup — fixes casing/punctuation and strips
          // filler words. Failure falls back to raw text (no user disruption).
          try {
            const { data: clean } = await api.post("/voice/clean", { text });
            if (clean?.cleaned) text = clean.cleaned;
          } catch { /* fall through with raw transcript */ }
          setBody((prev) => {
            if (!prev || prev.trim() === "" || prev.trim() === "<p></p>") return `<p>${text}</p>`;
            return `${prev}<p>${text}</p>`;
          });
          toast.success("Voice transcribed — cleaned and inserted");
        } catch (e) { toast.error(apiErrorMessage(e, "Transcription failed")); }
        finally { setTranscribing(false); }
      };
      mr.start();
      recRef.current = mr;
      setRecording(true);
    } catch {
      toast.error("Microphone access denied");
    }
  };

  const save = async (id) => {
    try {
      await api.patch(`/projects/${projectId}/status-updates/${id}`, { body_html: editBody });
      setEditId(null); toast.success("Saved"); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this update?")) return;
    try { await api.delete(`/projects/${projectId}/status-updates/${id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  const convertToTask = async (id) => {
    if (converting) return;
    setConverting(id);
    try {
      const { data } = await api.post(`/projects/${projectId}/status-updates/${id}/convert-to-task`);
      toast.success("Converted to task");
      load();
      // Optionally navigate: keep it inline so users stay in the feed.
      return data;
    } catch (e) {
      toast.error(apiErrorMessage(e, "Convert failed"));
    } finally {
      setConverting(null);
    }
  };

  const toggleMention = (uid) => {
    setMentionIds((prev) => prev.includes(uid) ? prev.filter((x) => x !== uid) : [...prev, uid]);
  };

  const memberOptions = members
    .map((m) => ({ id: m.user_id || m.id, name: m.user?.name || m.name || m.user?.email || m.email || "" }))
    .filter((m) => m.id && m.id !== user?.id && m.name)
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="space-y-4" data-testid="project-status-updates">
      <div className="border border-border p-3 bg-muted/20">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-semibold uppercase tracking-widest">Post update</div>
          <div className="flex items-center gap-1.5">
            <button type="button" onClick={() => setVisibility("internal")}
              className={`text-[10px] px-2 py-1 border flex items-center gap-1 ${visibility === "internal" ? "border-primary bg-primary/10" : "border-border hover:bg-muted"}`}
              data-testid="psu-visibility-internal"><Lock className="w-3 h-3" /> Internal</button>
            <button type="button" onClick={() => setVisibility("client")}
              className={`text-[10px] px-2 py-1 border flex items-center gap-1 ${visibility === "client" ? "border-primary bg-primary/10" : "border-border hover:bg-muted"}`}
              data-testid="psu-visibility-client"><Eye className="w-3 h-3" /> Visible to client</button>
          </div>
        </div>
        <SmartRichText
          value={body}
          onChange={setBody}
          mediaMode="upload"
          workspaceId={activeWorkspaceId || project?.workspace_id}
          minRows={3}
          maxHeight={340}
          placeholder="Write a status update. `/` for headings · `@` to notify a teammate (in-app only) · `/ai` to polish."
          testid="psu-body"
          mentionSuggestions={memberOptions.map((m) => ({ id: m.id, label: m.name }))}
          onMentionPick={(uid) => { if (!mentionIds.includes(uid)) setMentionIds((prev) => [...prev, uid]); }}
        />
        {/* R69 · Notify chips + picker — in-app notifications only. */}
        <div className="mt-2 relative">
          <div className="flex items-center gap-1.5 flex-wrap">
            <button
              type="button"
              onClick={() => setShowMentionPicker((v) => !v)}
              className="text-[10px] px-2 py-1 border border-border hover:bg-muted flex items-center gap-1"
              data-testid="psu-mention-toggle"
              title="Notify teammates — in-app only. Or type @ in the composer above."
            >
              @ Notify {mentionIds.length > 0 && <span className="ml-1 px-1 bg-primary text-primary-foreground font-bold">{mentionIds.length}</span>}
            </button>
            {mentionIds.map((uid) => {
              const m = memberOptions.find((x) => x.id === uid);
              return (
                <span key={uid} className="text-[10px] px-1.5 py-0.5 border border-primary/40 bg-primary/10 text-primary flex items-center gap-1" data-testid={`psu-mention-chip-${uid}`}>
                  @{m?.name || uid.slice(0, 6)}
                  <button type="button" onClick={() => toggleMention(uid)} className="hover:text-destructive" aria-label={`Remove ${m?.name || "mention"}`}><X className="w-2.5 h-2.5" /></button>
                </span>
              );
            })}
          </div>
          {showMentionPicker && (
            <div className="absolute left-0 top-full mt-1 z-20 border border-border bg-background shadow-xl max-h-56 overflow-y-auto w-64" data-testid="psu-mention-picker">
              {memberOptions.length === 0 ? (
                <div className="p-3 text-xs text-muted-foreground text-center">No teammates found.</div>
              ) : (
                memberOptions.map((m) => (
                  <button
                    key={m.id}
                    type="button"
                    onClick={() => toggleMention(m.id)}
                    className={`w-full text-left px-3 py-1.5 text-xs hover:bg-muted flex items-center gap-2 border-b border-border/40 last:border-b-0 ${mentionIds.includes(m.id) ? "bg-primary/10" : ""}`}
                    data-testid={`psu-mention-option-${m.id}`}
                  >
                    <span className={`w-3 h-3 border ${mentionIds.includes(m.id) ? "bg-primary border-primary" : "border-border"}`} />
                    <span className="truncate">{m.name}</span>
                  </button>
                ))
              )}
              <div className="px-3 py-2 border-t border-border bg-muted/30 text-[10px] text-muted-foreground">
                Notifications are <strong>in-app only</strong> — no email or WhatsApp.
              </div>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          {/* R73 · Voice-to-text button — dictate into the composer. */}
          <Button
            variant="outline"
            size="sm"
            className={`rounded-none ${recording ? "border-rose-500 text-rose-700 bg-rose-50" : ""}`}
            onClick={toggleRecord}
            disabled={transcribing}
            data-testid="psu-voice-toggle"
            title={recording ? "Click to stop recording and transcribe" : "Dictate the update — click to start"}
          >
            {transcribing ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Transcribing…</>
              : recording ? <><Square className="w-3.5 h-3.5 mr-1" /> Stop</>
              : <><Mic className="w-3.5 h-3.5 mr-1" /> Voice</>}
          </Button>
          <Button variant="outline" size="sm" className="rounded-none" onClick={aiDraft} disabled={drafting} data-testid="psu-ai-draft">
            {drafting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />} Draft with AI
          </Button>
          <Button variant="outline" size="sm" className="rounded-none" onClick={postWeeklyDigest} disabled={digesting} data-testid="psu-weekly-digest" title="Compose a deterministic Friday-style digest from the last 7 days">
            {digesting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <CalendarClock className="w-3.5 h-3.5 mr-1" />} Weekly digest
          </Button>
          <Button size="sm" className="rounded-none ml-auto" onClick={post} disabled={saving || !body.trim()} data-testid="psu-post">
            {saving ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Posting…</> : "Post update"}
          </Button>
        </div>
      </div>
      {loading ? (
        <div className="text-xs text-muted-foreground p-4">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="text-xs text-muted-foreground italic p-4 border border-dashed border-border">No updates yet. Post one above or try Draft with AI.</div>
      ) : (
        <ul className="space-y-3">
          {rows.map((u) => (
            <li key={u.id} className="border border-border p-3" data-testid={`psu-item-${u.id}`}>
              <div className="flex items-center gap-2 mb-1.5 text-xs">
                <strong>{u.author_name || "—"}</strong>
                <span className="text-muted-foreground">· {fmtRelative(u.created_at)}</span>
                <span className={`chip text-[10px] ${u.visibility === "client" ? "chip-approved" : ""}`}>{u.visibility === "client" ? "client-visible" : "internal"}</span>
                {u.kind === "ai_draft" && <span className="chip text-[10px]"><Sparkles className="w-3 h-3 inline" /> AI draft</span>}
                <div className="ml-auto flex items-center gap-1">
                  {!u.converted_task_id ? (
                    <button
                      type="button"
                      onClick={() => convertToTask(u.id)}
                      disabled={converting === u.id}
                      className="text-[10px] px-1.5 py-0.5 border border-border hover:bg-muted flex items-center gap-1"
                      title="Turn this update into a task on the same project"
                      data-testid={`psu-convert-${u.id}`}
                    >
                      {converting === u.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <CheckSquare className="w-3 h-3" />}
                      Convert to task
                    </button>
                  ) : (
                    <span className="text-[10px] px-1.5 py-0.5 border border-emerald-400/40 bg-emerald-50 text-emerald-700 flex items-center gap-1" data-testid={`psu-converted-${u.id}`}>
                      <CheckSquare className="w-3 h-3" /> Converted
                    </span>
                  )}
                  {u.author_id === user?.id && editId !== u.id && (
                    <button type="button" onClick={() => { setEditId(u.id); setEditBody(u.body_html); }} className="text-foreground/60 hover:text-foreground" data-testid={`psu-edit-${u.id}`}><Edit3 className="w-3.5 h-3.5" /></button>
                  )}
                  {u.author_id === user?.id && (
                    <button type="button" onClick={() => del(u.id)} className="text-destructive" data-testid={`psu-del-${u.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                  )}
                </div>
              </div>
              {editId === u.id ? (
                <>
                  <SmartRichText value={editBody} onChange={setEditBody} mediaMode="url-only" workspaceId={activeWorkspaceId} minRows={3} maxHeight={300} testid={`psu-edit-body-${u.id}`} />
                  <div className="flex gap-2 mt-2">
                    <Button size="sm" className="rounded-none" onClick={() => save(u.id)} data-testid={`psu-save-${u.id}`}><Save className="w-3 h-3 mr-1" /> Save</Button>
                    <Button size="sm" variant="outline" className="rounded-none" onClick={() => setEditId(null)}><X className="w-3 h-3 mr-1" /> Cancel</Button>
                  </div>
                </>
              ) : (
                <SmartRichTextDisplay html={u.body_html} testid={`psu-view-${u.id}`} />
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Tasks / Issues / Goals filtered views ────────────────────────────────

function FilteredList({ endpoint, kind, empty, project, itemLink, columns, filter, listKey }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true); setError(null);
      try {
        const r = await api.get(endpoint);
        let list;
        if (Array.isArray(r.data)) list = r.data;
        else if (listKey && Array.isArray(r.data?.[listKey])) list = r.data[listKey];
        else list = r.data?.items || r.data?.tasks || r.data?.goals || r.data?.issues || [];
        if (filter) list = list.filter(filter);
        if (!cancelled) setRows(list);
      } catch (e) {
        if (!cancelled) { setError(apiErrorMessage(e, "Failed to load")); console.error("_FilteredList error", endpoint, e); }
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [endpoint]); // eslint-disable-line
  if (loading) return <div className="text-xs text-muted-foreground p-4">Loading {kind}…</div>;
  if (error) return <div className="text-xs text-destructive p-4 border border-destructive/40 bg-destructive/5" data-testid={`project-${kind}-error`}>{error}</div>;
  if (rows.length === 0) return <div className="text-xs text-muted-foreground italic p-4 border border-dashed border-border">{empty}</div>;
  return (
    <div className="border border-border" data-testid={`project-${kind}-list`}>
      <table className="w-full text-xs">
        <thead className="bg-muted/40">
          <tr>{columns.map((c) => <th key={c.key} className="text-left px-3 py-2 font-semibold uppercase tracking-widest text-[10px]">{c.label}</th>)}<th></th></tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={r.id || i} className="border-t border-border hover:bg-muted/30" data-testid={`project-${kind}-row-${i}`}>
              {columns.map((c) => <td key={c.key} className="px-3 py-2 align-top">{c.render ? c.render(r) : (r[c.key] || "—")}</td>)}
              <td className="px-3 py-2">
                {itemLink && <Link to={itemLink(r)} className="text-primary hover:underline inline-flex items-center gap-1">Open <ArrowUpRight className="w-3 h-3" /></Link>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {project && <div className="px-3 py-1.5 text-[10px] text-muted-foreground border-t border-border">{rows.length} {kind} · project: {project.name}</div>}
    </div>
  );
}

/**
 * Feb 2026 · Visual skeleton loader for the Project > Tasks tab.
 *
 * Replaces the one-line "Loading…" placeholder with a shimmer-driven
 * fake table so the first paint feels instant and the user can tell
 * what's coming (header + ~10 rows with column skeleton bars). Pure
 * CSS — no deps.
 */
export function TaskSkeletonLoader({ rows = 10 }) {
  // Each bar is one of 5 lengths picked by a deterministic hash of the
  // row + column index so the output looks varied but doesn't jitter
  // on re-renders.
  const widths = ["30%", "55%", "40%", "70%", "50%", "60%", "45%", "35%"];
  const pick = (r, c) => widths[(r * 31 + c * 7) % widths.length];
  const cols = ["w-10", "w-[22%]", "w-[14%]", "w-[12%]", "w-[14%]", "w-[14%]", "w-[12%]"];
  return (
    <div className="border border-border bg-background" data-testid="project-tasks-skeleton">
      <style>{`@keyframes acqShimmer { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } } .acq-shimmer { background: linear-gradient(90deg, hsl(var(--muted)) 0%, hsl(var(--muted)/0.4) 50%, hsl(var(--muted)) 100%); background-size: 800px 100%; animation: acqShimmer 1.3s ease-in-out infinite; }`}</style>
      {/* Header row */}
      <div className="flex items-center gap-3 px-3 py-2 border-b border-border bg-muted/40">
        {cols.map((w, i) => (
          <div key={i} className={`${w} h-2.5 bg-muted-foreground/20 rounded-sm`} />
        ))}
      </div>
      {/* Data rows */}
      {Array.from({ length: rows }).map((_, r) => (
        <div key={r} className="flex items-center gap-3 px-3 py-2 border-b border-border/60 last:border-b-0">
          {cols.map((w, c) => (
            <div key={c} className={`${w} h-3 acq-shimmer rounded-sm`} style={{ opacity: 0.4 + (c === 0 ? 0 : 0.1) }}>
              <div style={{ width: pick(r, c), height: "100%" }} />
            </div>
          ))}
        </div>
      ))}
      <div className="px-3 py-2 text-[11px] text-muted-foreground border-t border-border bg-muted/30 flex items-center gap-2">
        <span className="inline-block w-3 h-3 rounded-full border-2 border-primary border-t-transparent animate-spin" aria-hidden="true" />
        <span>Loading tasks… <span className="opacity-60">this usually takes under a second.</span></span>
      </div>
    </div>
  );
}

export function ProjectTasksSubTab({ projectId, project }) {
  const { activeWorkspaceId } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [members, setMembers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);
  // Feb 2026 · Split loading state so we only show the skeleton loader
  // on the FIRST paint (no cached tasks). On subsequent refetches —
  // e.g. a debounced search — we keep the previous rows visible and
  // show a thin top progress bar instead (stale-while-revalidate).
  // Kills the ~400ms "flash of empty" every time the user types.
  const [firstLoad, setFirstLoad] = useState(true);
  const [refetching, setRefetching] = useState(false);
  // Legacy alias kept for other sections of this component.
  const busy = firstLoad || refetching;
  const [detailTask, setDetailTask] = useState(null);
  const [showBulk, setShowBulk] = useState(false);
  // R138bf · Quick search across all task views (title, description,
  // assignee, comment thread). Debounced so users can type freely.
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");
  // Feb 2026 · Structured filter chip row. Independent of the quick
  // search so a PM can combine "assignee=Me" + "overdue" + "q:brief".
  // All multi-select to allow unions (e.g. two statuses).
  // Feb 2026 R293h · Default status = "everything except done" so a PM
  // opening the tab lands straight on actionable work. Persisted per
  // project so a user who clears the filter stays on their choice.
  const [statusFilter, setStatusFilter] = useState(() => {
    try {
      const saved = localStorage.getItem(`acq-project-tasks-status-${projectId}`);
      if (saved) return JSON.parse(saved);
    } catch { /* ignore */ }
    return STATUSES.filter((s) => s !== "done");
  });
  useEffect(() => {
    try { localStorage.setItem(`acq-project-tasks-status-${projectId}`, JSON.stringify(statusFilter)); }
    catch { /* ignore */ }
  }, [projectId, statusFilter]);
  // Feb 2026 R293h · Mirror the main Tasks module's quick-scope
  // selector so a PM can narrow the project view to their own slice
  // (Assigned to me / Created by me / Watching / Mentioning me). "all"
  // keeps every task in the project visible.
  const [scope, setScope] = useState(() => {
    try { return localStorage.getItem(`acq-project-tasks-scope-${projectId}`) || "all"; }
    catch { return "all"; }
  });
  useEffect(() => {
    try { localStorage.setItem(`acq-project-tasks-scope-${projectId}`, scope); }
    catch { /* ignore */ }
  }, [projectId, scope]);
  const [priorityFilter, setPriorityFilter] = useState([]);
  const [assigneeFilter, setAssigneeFilter] = useState([]);
  // Feb 2026 R292w · Multi-select category filter — '' entry means
  // "Uncategorised" so admins can isolate uncategorised debt.
  const [categoryFilter, setCategoryFilter] = useState([]);
  const [dueFilter, setDueFilter] = useState("");        // "" | "overdue" | "today" | "this_week" | "no_due"
  const [healthFilter, setHealthFilter] = useState([]);  // ["on_track","at_risk","off_track"]
  const activeFilterCount =
    statusFilter.length + priorityFilter.length + assigneeFilter.length
    + categoryFilter.length + healthFilter.length + (dueFilter ? 1 : 0);
  const clearFilters = () => {
    setStatusFilter([]); setPriorityFilter([]); setAssigneeFilter([]);
    setCategoryFilter([]);
    setDueFilter(""); setHealthFilter([]);
  };
  useEffect(() => {
    const h = setTimeout(() => setDebouncedQuery(searchQuery.trim()), 250);
    return () => clearTimeout(h);
  }, [searchQuery]);
  const [unplannedOpen, setUnplannedOpen] = useState(() => {
    try { return localStorage.getItem(`acq-unplanned-open-${projectId}`) !== "0"; }
    catch { return true; }
  });
  const toggleUnplanned = () => {
    const next = !unplannedOpen;
    setUnplannedOpen(next);
    try { localStorage.setItem(`acq-unplanned-open-${projectId}`, next ? "1" : "0"); } catch { /* ignore */ }
  };

  // Feb 2026 · Members + Projects are workspace-wide, don't depend on
  // the search query, and never change inside this tab's lifetime.
  // Lift them to a one-shot fetch so a user typing in the quick search
  // isn't re-pulling two unrelated collections on every keystroke
  // (saved ~150-300ms per debounce tick on larger workspaces).
  useEffect(() => {
    if (!activeWorkspaceId) return;
    let alive = true;
    Promise.all([
      api.get(`/workspaces/${activeWorkspaceId}/members`).catch(() => ({ data: [] })),
      api.get(`/workspaces/${activeWorkspaceId}/projects`).catch(() => ({ data: [] })),
    ]).then(([m, p]) => {
      if (!alive) return;
      setMembers(m.data || []);
      setProjects(p.data || []);
    });
    return () => { alive = false; };
  }, [activeWorkspaceId]);

  const load = async () => {
    if (!activeWorkspaceId) return;
    if (tasks.length) setRefetching(true); else setFirstLoad(true);
    try {
      // R138bf · When a quick-search query is present we ONLY use
      // tasks-search (backend covers title / description / assignee /
      // comment thread). Falls back to tasks-with-risk otherwise so
      // rich risk annotations remain available in the default view.
      // R268yyyy · Feb 2026 — Send `lean=1` so heavy embedded arrays
      // (comments, timesheet_entries, checklist, subtasks, activities,
      // review snapshots) don't ship on every list render — killed
      // 300-900 ms off first paint on projects with 200+ tasks.
      // Feb 2026 R293h · Push the quick-scope selector to the server so
      // the row cap can be used for actionable work (assigned-me,
      // created-by-me, watching, mentioning-me) instead of filtering
      // down after a 500-row payload.
      const qParam = debouncedQuery ? `&q=${encodeURIComponent(debouncedQuery)}` : "";
      const scopeParam = scope && scope !== "all" ? `&scope=${encodeURIComponent(scope)}` : "";
      const listReq = debouncedQuery
        ? api.get(`/workspaces/${activeWorkspaceId}/tasks-search?project_id=${projectId}&limit=500${qParam}${scopeParam}`)
        : api.get(`/workspaces/${activeWorkspaceId}/tasks-with-risk?project_id=${projectId}&limit=500&lean=1${scopeParam}`)
            .catch(() => api.get(`/workspaces/${activeWorkspaceId}/tasks-search?project_id=${projectId}&limit=200${scopeParam}`));
      const t = await listReq;
      setTasks(t.data || []);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load tasks")); }
    finally { setFirstLoad(false); setRefetching(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId, projectId, debouncedQuery, scope]);

  // R148j · Feb 2026 — Client-side wide match across every text-ish field
  // so typing a status, priority, tag, brand, package etc. also filters
  // even though the backend `q` primarily hits title/description/comments.
  const filteredTasks = useMemo(() => {
    if (!debouncedQuery) return tasks;
    const parts = debouncedQuery.toLowerCase().split(/\s+/).filter(Boolean);
    if (!parts.length) return tasks;
    const stripHtmlLocal = (h) => (h || "").replace(/<[^>]*>/g, " ");
    return tasks.filter((t) => {
      const hay = [
        t.title, t.id, t.task_number, t.status, t.priority, t.health,
        t.risk_level, t.package_name, t.project_name, t.client_name,
        t.brand_name, t.assignee_name, t.assignee_email, t.reporter_name,
        t.reviewer_name, t.milestone_name, t.source, t.type,
        stripHtmlLocal(t.description), t.notes, t.close_notes,
        (t.tags || []).join(" "),
        (t.subtasks || []).map((s) => s?.title || "").join(" "),
        t.latest_comment_body,
        t.start_date, t.due_date,
      ].map((v) => (v == null ? "" : String(v).toLowerCase())).join(" · ");
      return parts.every((p) => hay.includes(p));
    });
  }, [tasks, debouncedQuery]);

  // R268yyyy · Feb 2026 — Optimistic add: prepend the created task to
  // local state so the row appears instantly; skip a full refetch.
  const addTaskLocal = (created) => {
    if (!created || !created.id) return;
    setTasks((prev) => {
      // Avoid duplicates when the server also fires a websocket refresh.
      if (prev.some((t) => t.id === created.id)) return prev;
      return [created, ...prev];
    });
  };
  const patchTask = async (id, patch) => {
    // Feb 2026 R293i · Date-change justification prompt. The backend
    // rejects any task-date edit (start/due/actual) without a `reason`
    // field once the task carries a previous value. Previously the
    // Project > Tasks subtab skipped this prompt and users saw a bare
    // red toast with no way to proceed — exactly the "popup didn't
    // show" bug reported. Mirror the Tasks module behaviour: ask once,
    // attach `date_change_reason`, let the backend persist with audit.
    const DATE_FIELDS = ["start_date", "due_date", "actual_start_date", "actual_end_date"];
    const prevTask = tasks.find((t) => t.id === id) || null;
    const changedDateKeys = DATE_FIELDS.filter((k) => {
      if (!(k in patch)) return false;
      const newV = patch[k] || null;
      const oldV = prevTask?.[k] || null;
      if (newV === oldV) return false;
      if (!oldV) return false;   // first-time set — no reason required
      return true;
    });
    if (changedDateKeys.length > 0 && !patch.date_change_reason) {
      // Feb 2026 R293j · Styled Dialog replaces the native window.prompt
      // so the UX is consistent with the rest of the app (dense/dark,
      // keyboard-friendly, works properly on mobile).
      const reason = await askDateChangeReason(changedDateKeys);
      if (reason === null) return;          // user cancelled
      patch = { ...patch, date_change_reason: reason };
    }
    // R268yyyy · Feb 2026 — Optimistic patch: splice the row in place;
    // roll back on failure. Kills the 300-800ms full-list refetch that
    // froze the table after every single-field edit.
    let prevSnapshot = null;
    setTasks((curr) => {
      const idx = curr.findIndex((t) => t.id === id);
      if (idx === -1) return curr;
      prevSnapshot = curr[idx];
      const next = curr.slice();
      next[idx] = { ...curr[idx], ...patch };
      return next;
    });
    try {
      const { data } = await api.patch(`/tasks/${id}`, patch);
      if (data && data.id) {
        setTasks((curr) => curr.map((t) => (t.id === id ? { ...t, ...data } : t)));
      }
      return data;
    } catch (e) {
      if (prevSnapshot) {
        setTasks((curr) => curr.map((t) => (t.id === id ? prevSnapshot : t)));
      }
      toast.error(apiErrorMessage(e, "Update failed"));
      // R268zzzz · Re-throw so callers (e.g. TaskDescriptionEditor)
      // don't silently mark drafts as saved after a backend rejection.
      throw e;
    }
  };

  // Iter Feb2026 · Phase strip state — active filter + group-by toggle
  // + loaded phases map (so we can hydrate phase_name on rows).
  const [activePhaseId, setActivePhaseId] = useState(null);
  const [groupByPhase, setGroupByPhase] = useState(false);
  const [phasesList, setPhasesList] = useState([]);
  const [kanbanGroupBy, setKanbanGroupBy] = useState("status");
  // Feb 2026 R292w · Task categories for Kanban "Category" swimlanes
  // + filter chip. Loaded once per workspace; silent-fallback on 403
  // so non-admin PMs still get a working Kanban.
  const [taskCategories, setTaskCategories] = useState([]);
  useEffect(() => {
    if (!activeWorkspaceId) return;
    api.get(`/workspaces/${activeWorkspaceId}/task-categories`)
      .then((r) => setTaskCategories(Array.isArray(r.data) ? r.data : []))
      .catch(() => setTaskCategories([]));
  }, [activeWorkspaceId]);
  const phasesById = useMemo(() => Object.fromEntries((phasesList || []).map((p) => [p.id, p])), [phasesList]);
  const { hasPerm } = useWorkspacePerms();
  const canManagePhases = hasPerm("workspace.admin") || hasPerm("workspace.manage")
    || hasPerm("project.manage") || hasPerm("task.manage");

  // R100 · Reuse the main Tasks list's rich column config so PMs see the
  // same fields (client / brand / priority / health / planned start-end
  // with inline edit / effort / billable etc.) inside a project's tab.
  // Feb 2026 · Build a denormalised id→name map from the loaded task
  // rows (backend writes `assignee_name` / `reporter_name` on each row
  // via `_enrich_task_names`). Prod bug: when an assigned teammate
  // isn't in the current workspace `members` list — cross-WS mover,
  // deactivated user, or stale cache — the picker fell back to the
  // raw UUID. Checking this map first eliminates that.
  const nameByTaskUserId = useMemo(() => {
    const m = {};
    for (const t of tasks || []) {
      if (t.assignee_id && t.assignee_name) m[t.assignee_id] = t.assignee_name;
      if (t.reporter_id && t.reporter_name) m[t.reporter_id] = t.reporter_name;
      if (t.created_by_id && t.created_by_name) m[t.created_by_id] = t.created_by_name;
    }
    return m;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tasks]);

  const memberName = (uid) => {
    if (!uid) return "";
    const m = members.find((x) => (x.user_id || x.id) === uid);
    if (m) {
      // Feb 2026 R293h · Also check the nested `.user` shape (some
      // callers of this list haven't been migrated to the Iter 253r
      // flattened fields yet). Mirrors the fallback in Tasks.jsx so the
      // Project > Tasks subtab never falls back to a bare UUID.
      return m.name || m.email || m.user?.name || m.user?.email || uid;
    }
    // Fall through to task-level denorm map before giving up with a UUID.
    return nameByTaskUserId[uid] || uid;
  };
  const canManageTask = () => true; // sub-tab is authenticated PM/admin surface

  // Iter Feb2026 · Hydrate phase_name on each row from the phases map,
  // then apply the active-phase filter. Declared BEFORE handleReorderTask
  // so the closure captures a defined binding.
  const scopedTasks = useMemo(() => {
    let list = filteredTasks;
    if (phasesList.length > 0 || list.some((t) => t.phase_id)) {
      list = list.map((t) => {
        const ph = t.phase_id ? phasesById[t.phase_id] : null;
        return ph ? { ...t, phase_name: ph.name } : t;
      });
    }
    if (activePhaseId !== null) {
      list = activePhaseId === "unphased"
        ? list.filter((t) => !t.phase_id)
        : list.filter((t) => t.phase_id === activePhaseId);
    }
    // Feb 2026 · Apply the structured filter chip row. All chips AND
    // together across dimensions but OR within a dimension (two
    // selected statuses both pass). `dueFilter` is a single value so
    // it just maps to a predicate.
    if (statusFilter.length) {
      const set = new Set(statusFilter);
      list = list.filter((t) => set.has(t.status));
    }
    if (priorityFilter.length) {
      const set = new Set(priorityFilter);
      list = list.filter((t) => set.has(t.priority));
    }
    if (assigneeFilter.length) {
      const set = new Set(assigneeFilter);
      list = list.filter((t) => {
        if (set.has("_unassigned")) {
          if (!t.assignee_id) return true;
        }
        return t.assignee_id && set.has(t.assignee_id);
      });
    }
    // Feb 2026 R292w · Category chip filter — empty-string entry means
    // "Uncategorised" so admins can clean up legacy rows.
    if (categoryFilter.length) {
      const set = new Set(categoryFilter);
      list = list.filter((t) => set.has(t.task_category_id || ""));
    }
    if (healthFilter.length) {
      const set = new Set(healthFilter);
      list = list.filter((t) => set.has(t.health || t.risk_level));
    }
    if (dueFilter) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const in7 = new Date(today);
      in7.setDate(in7.getDate() + 7);
      const todayISO = today.toISOString().slice(0, 10);
      const in7ISO = in7.toISOString().slice(0, 10);
      list = list.filter((t) => {
        const d = t.due_date;
        if (dueFilter === "no_due") return !d;
        if (!d) return false;
        if (dueFilter === "overdue") return d < todayISO && t.status !== "done";
        if (dueFilter === "today") return d === todayISO;
        if (dueFilter === "this_week") return d >= todayISO && d <= in7ISO;
        return true;
      });
    }
    return list;
  }, [filteredTasks, phasesById, activePhaseId, phasesList.length,
       statusFilter, priorityFilter, assigneeFilter, categoryFilter, healthFilter, dueFilter]);

  // Iter Feb2026 · Up/down reorder handler. Uses the fractional-index API
  // — sends the moving task's id + the target neighbour so backend
  // computes a mid-point order.
  const handleReorderTask = async (task, direction) => {
    if (!task || !task.phase_id) {
      toast.info("Assign a phase to reorder");
      return;
    }
    const phaseTasks = scopedTasks
      .filter((t) => t.phase_id === task.phase_id)
      .sort((a, b) => (a.phase_order ?? 0) - (b.phase_order ?? 0));
    const idx = phaseTasks.findIndex((t) => t.id === task.id);
    if (idx < 0) return;
    const targetIdx = direction === "up" ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= phaseTasks.length) return;
    const neighbour = phaseTasks[targetIdx];
    const body = direction === "up"
      ? { task_id: task.id, before_id: neighbour.id }
      : { task_id: task.id, after_id: neighbour.id };
    try {
      await api.patch(`/projects/${projectId}/phases/${task.phase_id}/tasks-reorder`, body);
      // Optimistic swap of phase_order values locally.
      setTasks((prev) => prev.map((t) => {
        if (t.id === task.id) return { ...t, phase_order: neighbour.phase_order };
        if (t.id === neighbour.id) return { ...t, phase_order: task.phase_order };
        return t;
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reorder failed");
    }
  };
  // Iter Feb2026 · Drag-and-drop reorder handler. Called by AdvancedTable
  // when a task <tr> is dropped onto another task <tr> WITHIN the same
  // phase (cross-phase drops are blocked at the row level by the
  // dragDropGroupKey guard).
  const handleRowDrop = async (fromId, toId, position) => {
    if (!fromId || !toId || fromId === toId) return;
    const from = scopedTasks.find((t) => t.id === fromId);
    const to = scopedTasks.find((t) => t.id === toId);
    if (!from || !to || from.phase_id !== to.phase_id || !from.phase_id) return;
    const body = position === "before"
      ? { task_id: fromId, before_id: toId }
      : { task_id: fromId, after_id: toId };
    try {
      const r = await api.patch(`/projects/${projectId}/phases/${from.phase_id}/tasks-reorder`, body);
      const newOrder = r?.data?.phase_order;
      if (typeof newOrder === "number") {
        setTasks((prev) => prev.map((t) => t.id === fromId ? { ...t, phase_order: newOrder } : t));
      } else {
        // Fallback: refetch tasks so the sort reflects the mid-point.
        load?.();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Reorder failed");
    }
  };

  // Feb 2026 R293h · Resilient members list. When the live roster
  // (/workspaces/{wsId}/members) hasn't resolved yet, has 0 rows, or
  // fails entirely in production, fall back to a synthesised list built
  // from the task rows themselves (every task carries `assignee_id` +
  // `assignee_name` after backend enrichment). This guarantees the
  // Assignee dropdown ALWAYS has at least the people who already have
  // a task on this project — eliminating the "No teammates found" dead
  // end the user hit on the deployed app.
  const resolvedMembers = useMemo(() => {
    if (members && members.length) return members;
    const seen = new Set();
    const synth = [];
    for (const t of tasks || []) {
      const uid = t.assignee_id;
      if (!uid || seen.has(uid)) continue;
      seen.add(uid);
      synth.push({
        user_id: uid,
        name: t.assignee_name || uid,
        email: t.assignee_email || "",
        status: "active",
      });
    }
    return synth;
  }, [members, tasks]);

  const projectsById = useMemo(() => {
    const m = {};
    for (const p of projects || []) m[p.id] = p;
    return m;
  }, [projects]);

  const projectTaskColumns = useMemo(() => {
    const cols = buildTaskColumns({
      members: resolvedMembers, patchTask, canManageTask, memberName,
      InlineDateCell, isOverdue, PRIORITIES, STATUSES,
      fmtRelative, PersonChip, TaskTitleCell,
      projectsById,
      onOpenTask: (t) => setDetailTask(t),
      onReorderTask: groupByPhase ? handleReorderTask : null,
      onCommentPosted: () => load(),
      // eslint-disable-next-line react-hooks/exhaustive-deps
    });
    // Iter Feb2026 · Force the Phase column visible when viewing tasks
    // inside a project. On the main /app/tasks page it stays hidden by
    // default so the table isn't cluttered for cross-project views.
    return cols.map((c) => c.key === "phase_name" ? { ...c, visible: true } : c);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [resolvedMembers, groupByPhase, projectsById]);

  if (!activeWorkspaceId) return null;
  return (
    <div className="space-y-1.5 relative" data-testid="project-tasks-subtab">
      {/* Feb 2026 · Thin top progress bar during a background refetch
          (search debounce, hidden-toggle, etc.) so the user sees that
          something is happening without the whole table disappearing.
          Pure CSS animated bar; no JS tick. */}
      {refetching && !firstLoad && (
        <div className="absolute left-0 right-0 top-0 h-0.5 overflow-hidden z-20" aria-hidden="true" data-testid="project-tasks-topbar">
          <div className="h-full w-1/3 bg-primary animate-[tasksbar_1.2s_ease-in-out_infinite]" />
        </div>
      )}
      <style>{`@keyframes tasksbar { 0% { transform: translateX(-100%); } 50% { transform: translateX(140%); } 100% { transform: translateX(340%); } }`}</style>
      {/* Feb 2026 R293p · One compact toolbar row — task count, quick
          search, filter chips, and icon-only Bulk / Quick-add / All-
          tasks actions all share a single flex row so the header
          reclaims one tab of vertical space. */}
      <ProjectTasksFilterRow
        tasks={tasks}
        members={members}
        scope={scope}
        setScope={setScope}
        statusFilter={statusFilter}
        setStatusFilter={setStatusFilter}
        priorityFilter={priorityFilter}
        setPriorityFilter={setPriorityFilter}
        assigneeFilter={assigneeFilter}
        setAssigneeFilter={setAssigneeFilter}
        categoryFilter={categoryFilter}
        setCategoryFilter={setCategoryFilter}
        taskCategories={taskCategories}
        dueFilter={dueFilter}
        setDueFilter={setDueFilter}
        healthFilter={healthFilter}
        setHealthFilter={setHealthFilter}
        activeFilterCount={activeFilterCount}
        onClear={clearFilters}
        // Inline count + search
        tasksCountNode={
          <span data-testid="project-tasks-count"
                className="inline-flex items-center gap-1 text-[11px] text-muted-foreground font-medium whitespace-nowrap">
            <CheckSquare className="w-3 h-3" />
            {firstLoad
              ? "Loading…"
              : debouncedQuery
                ? `${filteredTasks.length}/${tasks.length}`
                : `${tasks.length} tasks`}
            {refetching && !firstLoad && <span className="text-[10px] opacity-70">· refreshing…</span>}
          </span>
        }
        searchNode={
          <div className="relative">
            <SearchIcon className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search tasks…"
              className="h-7 pl-6 pr-6 text-xs bg-background border border-border rounded-none focus:outline-none focus:border-primary w-[200px]"
              data-testid="project-tasks-quick-search"
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => setSearchQuery("")}
                className="absolute right-1 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                title="Clear search"
                data-testid="project-tasks-quick-search-clear"
              >
                <X className="w-3 h-3" />
              </button>
            )}
          </div>
        }
        actionsNode={
          <>
            <button
              type="button"
              onClick={() => setShowBulk(true)}
              title="Bulk create tasks"
              aria-label="Bulk create tasks"
              className="inline-flex items-center justify-center h-7 w-7 border border-border bg-background hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              data-testid="project-tasks-bulk-btn"
            >
              <LayoutGrid className="w-3.5 h-3.5" />
            </button>
            <QuickAddTaskPopover
              wsId={activeWorkspaceId}
              preset={{ project_id: projectId }}
              projects={projects}
              members={members}
              onCreated={addTaskLocal}
              testid="project-tasks-quickadd"
              trigger={
                <button
                  type="button"
                  title="Quick add task"
                  aria-label="Quick add task"
                  className="inline-flex items-center justify-center h-7 w-7 border border-primary bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                  data-testid="project-tasks-quickadd"
                >
                  <Plus className="w-3.5 h-3.5" />
                </button>
              }
            />
            <Link
              to="/app/tasks"
              title="Open in full Tasks screen"
              aria-label="Open in full Tasks screen"
              className="inline-flex items-center justify-center h-7 w-7 border border-border bg-background hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
              data-testid="project-tasks-open-full"
            >
              <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </>
        }
      />
      <TaskViewSwitcher
        tasks={scopedTasks}
        members={members}
        patchTask={patchTask}
        onOpenTask={(t) => setDetailTask(t)}
        storageKey={`project-${projectId}-task-view`}
        defaultView="list"
        wsId={activeWorkspaceId}
        projectId={projectId}
        projects={projects}
        selectedIds={selectedIds}
        setSelectedIds={setSelectedIds}
        onCreated={addTaskLocal}
        kanbanGroupBy={kanbanGroupBy}
        onKanbanGroupByChange={setKanbanGroupBy}
        phases={phasesList}
        taskCategories={taskCategories}
        renderBilling={() => (
          <TaskBillingView wsId={activeWorkspaceId} projectId={projectId} onOpenTask={(t) => setDetailTask(t)} />
        )}
        renderList={() => (
          <div data-testid="project-tasks-advanced-table">
            <QuickAddTaskInline
              wsId={activeWorkspaceId}
              preset={{ project_id: projectId }}
              projects={projects}
              members={members}
              onCreated={addTaskLocal}
              testid="project-tasks-inline-add"
            />
            {filteredTasks.length === 0 ? (
              <div className="rounded-none border border-border bg-card p-6 text-center text-sm text-muted-foreground mt-3">
                {debouncedQuery ? `No matches for "${debouncedQuery}".` : "No tasks for this project yet."}
              </div>
            ) : (
              <>
                <ProjectPhasesStrip
                  wsId={activeWorkspaceId}
                  projectId={projectId}
                  activePhaseId={activePhaseId}
                  onActivePhaseChange={setActivePhaseId}
                  groupByPhase={groupByPhase}
                  onGroupByPhaseChange={setGroupByPhase}
                  onPhasesLoaded={setPhasesList}
                  canEdit={canManagePhases}
                />
                {firstLoad ? (
                  <TaskSkeletonLoader />
                ) : (
                <AdvancedTable
                  rows={scopedTasks}
                  columns={projectTaskColumns}
                  rowKey={(t) => t.id}
                  onRowClick={(t) => setDetailTask(t)}
                  /* Feb 2026 R293j · Shared tableKey across all projects so
                     a saved view (filters / columns / grouping) built on
                     one project is available on every project's Tasks
                     subtab. Legacy per-project views are migrated once
                     at startup by migrations.iter_r293j_shared_views. */
                  tableKey="project-task-table"
                  title="tasks"
                  // Oct 2026 · Compact + virtualize so PMs can scan more
                  // tasks per screen in dense projects without losing
                  // scroll performance.
                  compact
                  virtualizeRows={(scopedTasks?.length || 0) > 200}
                  selectable
                  selectedIds={new Set(selectedIds)}
                  onSelectionChange={(set) => setSelectedIds(Array.from(set))}
                  groupByOverride={groupByPhase ? "phase_name" : null}
                  onRowDrop={groupByPhase ? handleRowDrop : null}
                  dragDropGroupKey={groupByPhase ? ((t) => t.phase_id || "_unphased_") : null}
                />
                )}
              </>
            )}
          </div>
        )}
      />
      <BulkActionBar
        wsId={activeWorkspaceId}
        selectedIds={selectedIds}
        members={members}
        projects={projects}
        allTasks={tasks}
        onCleared={() => setSelectedIds([])}
        onApplied={load}
      />
      {showBulk && (
        <BulkTaskCreator
          open={showBulk}
          onClose={() => setShowBulk(false)}
          wsId={activeWorkspaceId}
          projects={projects}
          members={members}
          presetDefaults={{ project_id: projectId }}
          onCreated={() => { setShowBulk(false); load(); }}
        />
      )}
      <UnplannedTasksPanel
        tasks={tasks}
        open={unplannedOpen}
        onToggle={toggleUnplanned}
        onOpenTask={setDetailTask}
        patchTask={patchTask}
      />
      {detailTask && (
        <TaskDetail
          /* Feb 2026 R293h · Keep the drawer live. Previously we passed
             the `detailTask` snapshot captured at open time; any inline
             edit (title, priority, assignee, due-date, etc.) ran through
             `patchTask`, which updated the row in the `tasks` list but
             NOT this snapshot — so the field only "jumped" after the
             user closed and re-opened the drawer. Deriving the task
             from the always-fresh `tasks` list makes every edit visible
             instantly. Falls back to the snapshot when the row has been
             removed from the list (e.g. filtered out). */
          task={tasks.find((t) => t.id === detailTask.id) || detailTask}
          members={resolvedMembers}
          allTasks={tasks}
          onClose={() => setDetailTask(null)}
          onPatch={async (patch) => { await patchTask(detailTask.id, patch); }}
        />
      )}
    </div>
  );
}

// Feb 2026 · Compact filter row for Project > Tasks. Each chip renders
// a Popover with a multi-select checkbox list. All filters are
// client-side and composable with the quick-search above.
function ProjectTasksFilterRow({
  tasks, members,
  scope, setScope,
  statusFilter, setStatusFilter,
  priorityFilter, setPriorityFilter,
  assigneeFilter, setAssigneeFilter,
  categoryFilter, setCategoryFilter, taskCategories = [],
  dueFilter, setDueFilter,
  healthFilter, setHealthFilter,
  activeFilterCount, onClear,
  // Feb 2026 R293p · Consolidated toolbar slots so the Tasks sub-tab
  // renders count + search + filter chips + actions on a single row.
  tasksCountNode = null,
  searchNode = null,
  actionsNode = null,
}) {
  const toggleIn = (arr, setArr, v) =>
    setArr(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
  // Derived facets so counts reflect the current data (hide chips with 0 rows).
  const facet = (fn) => Array.from(new Set((tasks || []).map(fn).filter(Boolean)));
  // Feb 2026 R292t · Previously the Status chip only listed statuses
  // present in the currently-fetched slice, which meant users couldn't
  // chip "done" on an empty project or "blocked" when nothing was
  // blocked yet. Mirror the main Tasks page and show every status in
  // the global list — the row count on the chip already tells you if
  // a status has zero matches.
  const statusOptions = STATUSES;
  const priorityOptions = PRIORITIES.filter((p) => facet((t) => t.priority).includes(p));
  const assigneeOptions = (() => {
    const used = new Set((tasks || []).map((t) => t.assignee_id).filter(Boolean));
    const rows = (members || [])
      .filter((m) => used.has(m.user_id || m.id))
      .map((m) => ({ v: m.user_id || m.id, label: m.name || m.email || "Unknown" }))
      .sort((a, b) => a.label.localeCompare(b.label));
    if ((tasks || []).some((t) => !t.assignee_id)) {
      rows.unshift({ v: "_unassigned", label: "— Unassigned —" });
    }
    return rows;
  })();
  const DUE_CHOICES = [
    { v: "overdue",   label: "Overdue" },
    { v: "today",     label: "Due today" },
    { v: "this_week", label: "Due this week" },
    { v: "no_due",    label: "No due date" },
  ];
  const HEALTH_CHOICES = [
    { v: "on_track",  label: "On track" },
    { v: "at_risk",   label: "At risk" },
    { v: "off_track", label: "Off track" },
  ];
  return (
    <div className="flex items-center gap-1.5 flex-wrap text-xs" data-testid="project-tasks-filter-row">
      {tasksCountNode}
      {searchNode}
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground font-semibold flex items-center gap-1">
        Filters
        {activeFilterCount > 0 && (
          <span className="inline-block min-w-[16px] text-center bg-primary text-primary-foreground text-[9px] font-bold px-1 py-0 rounded-none"
                   data-testid="project-tasks-filter-count">
            {activeFilterCount}
          </span>
        )}
      </span>
      {/* Feb 2026 R293h · Scope selector — mirrors the main Tasks module
           so a PM can narrow a busy project to their own slice. Single-
           select; persisted per project in localStorage. */}
      <SingleFilterChip
        label="Scope"
        selected={scope === "all" ? "" : scope}
        options={[
          { v: "assigned-me",   label: "Assigned to me" },
          { v: "created-by-me", label: "Created by me" },
          { v: "watching",      label: "Watching" },
          { v: "mentioning-me", label: "Mentioning me" },
        ]}
        onChange={(v) => setScope(v || "all")}
        testid="project-tasks-filter-scope"
      />
      <FilterChip label="Status" selected={statusFilter} options={statusOptions.map((s) => ({ v: s, label: s.replace("_", " ") }))} onToggle={(v) => toggleIn(statusFilter, setStatusFilter, v)} onClear={() => setStatusFilter([])} onSetAll={setStatusFilter} testid="project-tasks-filter-status" />
      <FilterChip label="Priority" selected={priorityFilter} options={priorityOptions.map((p) => ({ v: p, label: p }))} onToggle={(v) => toggleIn(priorityFilter, setPriorityFilter, v)} onClear={() => setPriorityFilter([])} onSetAll={setPriorityFilter} testid="project-tasks-filter-priority" />
      <FilterChip label="Assignee" selected={assigneeFilter} options={assigneeOptions} onToggle={(v) => toggleIn(assigneeFilter, setAssigneeFilter, v)} onClear={() => setAssigneeFilter([])} onSetAll={setAssigneeFilter} testid="project-tasks-filter-assignee" />
      {/* Feb 2026 R292w · Category chip */}
      {taskCategories.length > 0 && (
        <FilterChip
          label="Category"
          selected={categoryFilter}
          options={[
            { v: "", label: "Uncategorised" },
            ...taskCategories.map((c) => ({ v: c.id, label: c.name })),
          ]}
          onToggle={(v) => toggleIn(categoryFilter, setCategoryFilter, v)}
          onClear={() => setCategoryFilter([])}
          onSetAll={setCategoryFilter}
          testid="project-tasks-filter-category"
        />
      )}
      {/* Due is single-select so we render as a Select-like chip. */}
      <SingleFilterChip label="Due" selected={dueFilter} options={DUE_CHOICES} onChange={setDueFilter} testid="project-tasks-filter-due" />
      <FilterChip label="Health" selected={healthFilter} options={HEALTH_CHOICES} onToggle={(v) => toggleIn(healthFilter, setHealthFilter, v)} onClear={() => setHealthFilter([])} onSetAll={setHealthFilter} testid="project-tasks-filter-health" />
      {activeFilterCount > 0 && (
        <button
          type="button"
          onClick={onClear}
          className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground border border-border bg-background px-2 py-1 ml-1"
          data-testid="project-tasks-filter-clear">
          Clear all
        </button>
      )}
      {actionsNode && (
        <div className="ml-auto flex items-center gap-1.5" data-testid="project-tasks-actions">
          {actionsNode}
        </div>
      )}
    </div>
  );
}

function FilterChip({ label, selected, options, onToggle, onClear, onSetAll, testid }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [sortMode, setSortMode] = useState("alpha"); // "alpha" | "selected"
  const ref = useRef(null);
  // Close on outside click so chips feel like Radix popovers.
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", h);
    return () => window.removeEventListener("mousedown", h);
  }, [open]);
  // Reset search text each time the popover opens.
  useEffect(() => { if (!open) setQ(""); }, [open]);
  if (!options.length) return null;
  const count = selected.length;

  // Feb 2026 R293s · Search filter + sort inside the popover. Sort
  // alphabetical by default; a toggle brings selected items to the
  // top so power users can quickly spot their current picks.
  const normalized = (s) => String(s || "").toLowerCase();
  const query = normalized(q.trim());
  const filtered = options.filter((o) =>
    !query ||
    normalized(o.label).includes(query) ||
    normalized(o.v).includes(query)
  );
  const sorted = [...filtered].sort((a, b) => {
    if (sortMode === "selected") {
      const sa = selected.includes(a.v) ? 0 : 1;
      const sb = selected.includes(b.v) ? 0 : 1;
      if (sa !== sb) return sa - sb;
    }
    return normalized(a.label).localeCompare(normalized(b.label));
  });

  const visibleValues = sorted.map((o) => o.v);
  const allVisibleSelected = visibleValues.length > 0 && visibleValues.every((v) => selected.includes(v));

  const handleSelectAll = () => {
    if (onSetAll) {
      // Union current selection with every visible option.
      const next = Array.from(new Set([...selected, ...visibleValues]));
      onSetAll(next);
    } else {
      // Fallback: toggle each not-yet-selected visible option.
      visibleValues.forEach((v) => { if (!selected.includes(v)) onToggle(v); });
    }
  };
  const handleUnselectAll = () => {
    if (onSetAll) {
      const vis = new Set(visibleValues);
      onSetAll(selected.filter((v) => !vis.has(v)));
    } else {
      visibleValues.forEach((v) => { if (selected.includes(v)) onToggle(v); });
    }
  };

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1 h-7 px-2 border rounded-none text-[11px] ${count ? "border-primary bg-primary/10 font-semibold" : "border-border bg-background hover:bg-muted/40"}`}
        data-testid={testid}>
        {label}
        {count > 0 && (
          <span className="inline-block min-w-[14px] text-center bg-primary text-primary-foreground text-[9px] font-bold px-1 rounded-none">
            {count}
          </span>
        )}
        <ChevronDown className="w-3 h-3 opacity-60" />
      </button>
      {open && (
        <div className="absolute z-30 top-full left-0 mt-1 w-64 bg-background border border-border shadow-lg">
          {/* Search box + sort toggle */}
          <div className="px-1.5 py-1.5 border-b border-border flex items-center gap-1">
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search…"
              className="flex-1 h-6 px-1.5 text-[11px] border border-border bg-background rounded-none focus:outline-none focus:border-primary"
              data-testid={`${testid}-search`}
              autoFocus
            />
            <button
              type="button"
              onClick={() => setSortMode((m) => (m === "alpha" ? "selected" : "alpha"))}
              className={`inline-flex items-center justify-center h-6 w-6 border rounded-none ${sortMode === "selected" ? "border-primary bg-primary/10 text-primary" : "border-border bg-background hover:bg-muted/40"}`}
              data-testid={`${testid}-sort`}
              title={sortMode === "alpha" ? "Sort: A→Z · click to show selected first" : "Showing selected first · click for A→Z"}
              aria-label="Toggle sort order"
            >
              <ArrowUp className="w-3 h-3" />
            </button>
          </div>
          {/* Select-all / Unselect-all */}
          <div className="flex items-center gap-1 px-1.5 py-1 border-b border-border">
            <button
              type="button"
              onClick={handleSelectAll}
              disabled={visibleValues.length === 0 || allVisibleSelected}
              className="flex-1 h-6 text-[10px] uppercase tracking-widest border border-border bg-background hover:bg-muted/40 disabled:opacity-50 disabled:cursor-not-allowed rounded-none"
              data-testid={`${testid}-select-all`}>
              Select all
            </button>
            <button
              type="button"
              onClick={handleUnselectAll}
              disabled={count === 0}
              className="flex-1 h-6 text-[10px] uppercase tracking-widest border border-border bg-background hover:bg-muted/40 disabled:opacity-50 disabled:cursor-not-allowed rounded-none"
              data-testid={`${testid}-unselect-all`}>
              Unselect all
            </button>
          </div>
          <div className="max-h-56 overflow-y-auto py-1">
            {sorted.length === 0 ? (
              <div className="px-2 py-2 text-[11px] italic text-muted-foreground">No matches</div>
            ) : sorted.map((o) => {
              const on = selected.includes(o.v);
              return (
                <button
                  key={o.v}
                  type="button"
                  onClick={() => onToggle(o.v)}
                  className="w-full text-left px-2 py-1.5 text-[11px] hover:bg-muted/40 flex items-center gap-2"
                  data-testid={`${testid}-opt-${o.v}`}>
                  <input type="checkbox" readOnly checked={on} className="pointer-events-none" />
                  <span className="truncate">{o.label}</span>
                </button>
              );
            })}
          </div>
          {count > 0 && (
            <button
              type="button"
              onClick={() => { onClear(); setOpen(false); }}
              className="w-full border-t border-border px-2 py-1 text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground hover:bg-muted/40"
              data-testid={`${testid}-clear`}>
              Clear all
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function SingleFilterChip({ label, selected, options, onChange, testid }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    window.addEventListener("mousedown", h);
    return () => window.removeEventListener("mousedown", h);
  }, [open]);
  const activeLabel = selected ? (options.find((o) => o.v === selected)?.label || selected) : null;
  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1 h-7 px-2 border rounded-none text-[11px] ${selected ? "border-primary bg-primary/10 font-semibold" : "border-border bg-background hover:bg-muted/40"}`}
        data-testid={testid}>
        {label}
        {activeLabel && <span className="normal-case">· {activeLabel}</span>}
        <ChevronDown className="w-3 h-3 opacity-60" />
      </button>
      {open && (
        <div className="absolute z-30 top-full left-0 mt-1 w-48 bg-background border border-border shadow-lg py-1">
          <button
            type="button"
            onClick={() => { onChange(""); setOpen(false); }}
            className="w-full text-left px-2 py-1 text-[11px] hover:bg-muted/40 italic text-muted-foreground"
            data-testid={`${testid}-opt-any`}>
            — Any —
          </button>
          {options.map((o) => (
            <button
              key={o.v}
              type="button"
              onClick={() => { onChange(o.v); setOpen(false); }}
              className={`w-full text-left px-2 py-1 text-[11px] hover:bg-muted/40 ${selected === o.v ? "font-semibold" : ""}`}
              data-testid={`${testid}-opt-${o.v}`}>
              {o.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Iter 204 · Unplanned tasks side/bottom panel (collapsible, all views)
function UnplannedTasksPanel({ tasks, open, onToggle, onOpenTask, patchTask }) {
  const unplanned = (tasks || []).filter(
    (t) => !t.start_date && !t.due_date && t.status !== "done");
  return (
    <div className="border border-dashed border-primary/40 bg-primary/[0.03]" data-testid="unplanned-panel">
      <button
        type="button"
        onClick={onToggle}
        className="w-full px-3 py-2 flex items-center justify-between text-xs font-semibold uppercase tracking-widest hover:bg-primary/5"
        data-testid="unplanned-panel-toggle"
      >
        <span className="flex items-center gap-2">
          📋 Unplanned tasks
          <span className="chip chip-pending text-[10px] normal-case tracking-normal">{unplanned.length}</span>
        </span>
        <span className="text-muted-foreground">{open ? "▼" : "▶"}</span>
      </button>
      {open && (
        <div className="p-2 max-h-64 overflow-y-auto" data-testid="unplanned-panel-body">
          {unplanned.length === 0 ? (
            <div className="text-[11px] text-muted-foreground italic p-3 text-center">
              All tasks have a start or due date. 🎉
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1.5">
              {unplanned.map((t) => (
                <div
                  key={t.id}
                  draggable
                  onDragStart={(e) => {
                    try {
                      e.dataTransfer.setData("application/x-task-id", t.id);
                      e.dataTransfer.setData("text/plain", t.title || "");
                      e.dataTransfer.effectAllowed = "move";
                    } catch { /* noop */ }
                  }}
                  className="border border-border bg-card p-2 text-xs hover:border-primary cursor-grab active:cursor-grabbing flex items-center gap-2"
                  data-testid={`unplanned-task-${t.id}`}
                  title="Drag to a Calendar view · click to edit"
                >
                  <span
                    className={`chip text-[9px] flex-none ${
                      t.priority === "urgent" ? "chip-rejected"
                        : t.priority === "high" ? "chip-pending"
                        : "chip-internal"
                    }`}
                  >
                    {(t.priority || "med").slice(0, 3)}
                  </span>
                  <button
                    type="button"
                    onClick={() => onOpenTask?.(t)}
                    className="flex-1 text-left truncate hover:text-primary"
                    data-testid={`unplanned-task-open-${t.id}`}
                  >
                    {t.title || "(untitled)"}
                  </button>
                  <input
                    type="date"
                    onChange={(e) => {
                      const v = e.target.value;
                      if (v && patchTask) patchTask(t.id, { due_date: v });
                    }}
                    className="h-6 text-[10px] border border-border bg-background px-1 w-24"
                    title="Set a due date"
                    data-testid={`unplanned-task-due-${t.id}`}
                    onClick={(e) => e.stopPropagation()}
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function ProjectIssuesSubTab({ projectId, project }) {
  return <FilteredList
    endpoint={`/projects/${projectId}/raid`}
    kind="issues"
    empty="No issues logged. Use the RAID tab to add one."
    project={project}
    filter={(r) => (r.kind || r.type || "").toLowerCase() === "issue"}
    itemLink={() => `/app/projects/${projectId}?tab=raid`}
    columns={[
      { key: "title", label: "Title", render: (r) => <span className="font-semibold flex items-center gap-1"><Bug className="w-3 h-3" />{r.title}</span> },
      { key: "severity", label: "Severity", render: (r) => <span className="chip text-[10px]">{r.severity || r.priority || "—"}</span> },
      { key: "status", label: "Status", render: (r) => <span className="chip text-[10px]">{r.status || "—"}</span> },
      { key: "owner_name", label: "Owner" },
    ]}
  />;
}

export function ProjectGoalsSubTab({ projectId, project }) {
  const { activeWorkspaceId } = useAuth();
  const [showNew, setShowNew] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  if (!activeWorkspaceId) return null;
  return (
    <div className="space-y-4" data-testid="project-goals-subtab">
      <div className="flex items-center gap-2">
        <Target className="w-4 h-4 text-primary" />
        <span className="text-xs font-semibold">Goals owned by this project</span>
        <div className="ml-auto flex items-center gap-2">
          <Button size="sm" className="rounded-none h-8 text-xs" onClick={() => setShowNew(true)} data-testid="project-goals-new-btn">
            <Target className="w-3 h-3 mr-1" /> New goal
          </Button>
          <Link to="/app/goals" className="text-[10px] text-primary underline">Full Goals page →</Link>
        </div>
      </div>
      <ProjectGoalsCheckinBanner projectId={projectId} wsId={activeWorkspaceId} key={`banner-${refreshKey}`} />
      <FilteredList
        key={`list-${refreshKey}`}
        endpoint={`/workspaces/${activeWorkspaceId}/goals?owner_project_id=${projectId}&limit=200`}
        kind="goals"
        empty="No goals owned by this project yet. Click New goal above to add one."
        project={project}
        itemLink={(r) => `/app/goals?goal_id=${r.id}`}
        columns={[
          { key: "title", label: "Goal", render: (r) => <span className="font-semibold flex items-center gap-1"><Target className="w-3 h-3" />{r.title}</span> },
          { key: "progress", label: "Progress", render: (r) => `${Math.round((r.progress || 0) * 100)}%` },
          { key: "status", label: "Status", render: (r) => <span className="chip text-[10px]">{r.status || "—"}</span> },
          { key: "target_date", label: "Due" },
        ]}
      />
      {showNew && (
        <NewProjectGoalDialog
          wsId={activeWorkspaceId}
          projectId={projectId}
          onClose={() => setShowNew(false)}
          onCreated={() => { setShowNew(false); setRefreshKey((k) => k + 1); }}
        />
      )}
    </div>
  );
}

function NewProjectGoalDialog({ wsId, projectId, onClose, onCreated }) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [horizon, setHorizon] = useState("quarterly");
  const [endDate, setEndDate] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!title.trim()) { toast.error("Title required"); return; }
    setBusy(true);
    try {
      await api.post(`/workspaces/${wsId}/goals`, {
        title: title.trim(),
        description: description.trim() || null,
        horizon,
        owner_type: "project",
        owner_project_id: projectId,
        mode: "okr",
        progress_mode: "kr_weighted",
        end_date: endDate || null,
        status: "active",
      });
      toast.success("Goal created");
      onCreated?.();
    } catch (e) { toast.error(apiErrorMessage(e, "Create failed")); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose} data-testid="project-goal-new-dialog">
      <div className="bg-background border border-border w-full max-w-md rounded-none" onClick={(e) => e.stopPropagation()}>
        <div className="px-4 py-3 border-b border-border">
          <div className="font-bold text-sm inline-flex items-center gap-2"><Target className="w-4 h-4 text-primary" /> New goal for this project</div>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <label className="text-xs font-semibold">Title *</label>
            <input value={title} onChange={(e) => setTitle(e.target.value)} autoFocus className="w-full h-10 border border-border bg-background px-2 text-sm rounded-none" data-testid="project-goal-new-title" placeholder="e.g. Launch v2 to 100 beta users" />
          </div>
          <div>
            <label className="text-xs font-semibold">Description</label>
            <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)} className="w-full border border-border bg-background px-2 py-1 text-sm rounded-none" data-testid="project-goal-new-desc" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-xs font-semibold">Horizon</label>
              <select value={horizon} onChange={(e) => setHorizon(e.target.value)} className="w-full h-10 border border-border bg-background px-2 text-sm rounded-none" data-testid="project-goal-new-horizon">
                <option value="monthly">Monthly</option>
                <option value="quarterly">Quarterly</option>
                <option value="annual">Annual</option>
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold">Target date</label>
              <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-full h-10 border border-border bg-background px-2 text-sm rounded-none" data-testid="project-goal-new-target-date" />
            </div>
          </div>
        </div>
        <div className="px-4 py-3 border-t border-border flex justify-end gap-2">
          <button className="text-xs px-3 py-1.5 border border-border hover:bg-muted" onClick={onClose}>Cancel</button>
          <button className="text-xs px-3 py-1.5 bg-primary text-primary-foreground disabled:opacity-50" onClick={submit} disabled={busy || !title.trim()} data-testid="project-goal-new-save">
            {busy ? "Creating…" : "Create goal"}
          </button>
        </div>
      </div>
    </div>
  );
}

function ProjectGoalsCheckinBanner({ projectId, wsId }) {
  const [goals, setGoals] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [text, setText] = useState("");
  const [progress, setProgress] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get(`/workspaces/${wsId}/goals?owner_project_id=${projectId}&limit=50`)
      .then((r) => setGoals(r.data || []))
      .catch(() => {});
  }, [wsId, projectId]);
  if (goals.length === 0) return null;
  const selected = goals.find((g) => g.id === selectedId) || goals[0];
  const submit = async () => {
    if (!selected) return;
    if (!text.trim() && !progress) {
      toast.error("Add a note or a progress % to check in");
      return;
    }
    setBusy(true);
    try {
      const payload = { narrative: text.trim() || null, confidence: "on_track" };
      if (progress !== "") payload.progress_pct = Math.max(0, Math.min(100, Number(progress)));
      await api.post(`/workspaces/${wsId}/goals/${selected.id}/checkins`, payload);
      toast.success("Check-in posted");
      setText("");
      setProgress("");
    } catch (e) {
      toast.error(apiErrorMessage(e, "Couldn't post check-in"));
    } finally { setBusy(false); }
  };
  return (
    <div className="border border-primary/30 bg-primary/5 p-3 space-y-2" data-testid="project-goals-checkin-banner">
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-[10px] uppercase tracking-widest text-primary font-semibold">Post check-in</span>
        <select
          value={selected?.id || ""}
          onChange={(e) => setSelectedId(e.target.value)}
          className="h-8 border border-border bg-background rounded-none text-xs px-2 flex-1 min-w-[180px]"
          data-testid="project-goals-checkin-select"
        >
          {goals.map((g) => (
            <option key={g.id} value={g.id}>{g.title} — {Math.round((g.progress || 0) * 100)}%</option>
          ))}
        </select>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="How are we tracking on this goal today?"
          className="h-8 border border-border bg-background rounded-none text-xs px-2 flex-1 min-w-[200px]"
          data-testid="project-goals-checkin-note"
        />
        <input
          type="number"
          min={0}
          max={100}
          step={5}
          value={progress}
          onChange={(e) => setProgress(e.target.value)}
          placeholder="%"
          className="h-8 border border-border bg-background rounded-none text-xs px-2 w-20 font-mono"
          data-testid="project-goals-checkin-progress"
        />
        <button
          type="button"
          onClick={submit}
          disabled={busy}
          className="h-8 border border-primary bg-primary text-primary-foreground rounded-none text-xs px-3 hover:brightness-110 transition disabled:opacity-50"
          data-testid="project-goals-checkin-submit"
        >{busy ? "Posting…" : "Check in"}</button>
      </div>
    </div>
  );
}
